import {
  Children,
  createContext,
  useContext,
  useLayoutEffect,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import {
  setDeviceLocales,
  setFormatRegion,
  type AnyLocaleType,
  type FormatRegionChoiceType,
  type MessageArgs,
} from '@featit/shared/i18n/core';
import type { MessageKey } from '@featit/shared/i18n/messages/index';
import { createLiteTranslator, type ITranslator, type RichArgs } from '@featit/shared/i18n/lite';
import { ENGLISH, loadedMessages, loadMessages, reachMessages } from './catalogue';
import { languagePreference } from './languageInstance';
import { announcePageLanguage, clearPageTranslator, setPageTranslator } from './pageTranslator';

/**
 * The language of the open page and the one function that turns a message key into text (ADR-148).
 *
 * ⛔ A component asks `useT()` for its text and never writes a sentence of its own: the literal ratchet
 * (`scripts/i18n-literals.mjs`, IN-3) fails a file whose count of user-facing literals rises. Without a
 * provider, as in a unit test, the English catalogue answers, so a converted component renders exactly
 * what it did before it was converted.
 */
export type TFn = {
  (key: MessageKey, args?: MessageArgs): string;
  /**
   * A message with tags, as nodes: `t.rich('auth.register.sentLink', { email, address: (chunks) => <b>{chunks}</b> })`.
   * Each tag the message names is given a function from its content to a node.
   */
  rich: (key: MessageKey, args?: RichArgs) => ReactNode;
};

/** The shared translator as a screen uses it: its rich parts become nodes with keys. */
function forScreen(translator: ITranslator): TFn {
  const t = ((key, args) => translator(key, args)) as TFn;
  t.rich = (key, args) => Children.toArray(translator.rich(key, args) as ReactNode[]);
  return t;
}

interface ILocaleValue {
  locale: AnyLocaleType;
  t: TFn;
  /** F-0615: the picked region; a change re-renders every reader, so its dates and numbers follow. */
  region?: FormatRegionChoiceType;
  /** I-1971: the reader's language is known and its catalogue is still loading: the page shows another. */
  pending?: boolean;
}

// IN-6: the language is the person's setting; the region of a date or number is the device's, when it speaks
// that language (en-GB reads "22 September 2026", en-US "September 22, 2026").
if (typeof navigator !== 'undefined') setDeviceLocales(navigator.languages ?? [navigator.language]);

const english = createLiteTranslator(ENGLISH.locale, ENGLISH.messages);
const DEFAULT_VALUE: ILocaleValue = { locale: ENGLISH.locale, t: forScreen(english) };

/**
 * The English translator, where English is meant explicitly: tests and fixtures that read the
 * source-language sentences. ⛔ Never a default argument: a screen passes its page's `useT()`, or a
 * German page would draw English without anything saying so.
 */
export const englishT: TFn = DEFAULT_VALUE.t;
const LocaleContext = createContext<ILocaleValue>(DEFAULT_VALUE);

/**
 * Shows `locale`, which is the person's language by default (`languagePreference`: their saved choice, else
 * the browser's, else English) and changes in place when they choose another. A language that is not English loads its chunk first and
 * shows English until it is in (the shell's "cannot be reached" words in its own, I-1971), then re-renders in
 * place; `<html lang>` follows it.
 */
export function LocaleProvider({
  locale: given,
  children,
}: {
  locale?: AnyLocaleType;
  children: ReactNode;
}) {
  const resolved = useSyncExternalStore(languagePreference.subscribe, () =>
    languagePreference.resolved(),
  );
  const locale = given ?? resolved;
  // I-2088: the language a fetch has delivered since mount. A language already loaded (the boot reads the
  // person's before the first render) never waits for it: the value below reads it at once.
  const [loaded, setLoaded] = useState<{ locale: AnyLocaleType; messages: unknown } | null>(null);
  useEffect(() => {
    let current = true;
    // English is always here: the value below reads it directly.
    if (locale === 'en') return;
    loadMessages(locale).then(
      (messages) => {
        if (current) setLoaded({ locale, messages });
      },
      // I-2088: a chunk that cannot be fetched leaves the language already shown (English at first): the
      // page still reads, and nothing waits for it.
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [locale]);
  const value = useMemo<ILocaleValue>(() => {
    // I-2088: a language already loaded is drawn at once, so a switch to it is one commit. While another
    // loads, the one already shown stays: a switch never drops to English.
    const here = loadedMessages(locale);
    const shown = here !== undefined ? { locale, messages: here } : loaded;
    if (!shown) {
      // I-1971: the language's chunk is not here (it is loading, or its fetch failed, which a browser keeps
      // for the life of the page): English, but the shell's "cannot be reached" words in the reader's.
      const reach = reachMessages(locale);
      const pending = locale !== ENGLISH.locale;
      if (reach === undefined) return { ...DEFAULT_VALUE, pending };
      return {
        locale: ENGLISH.locale,
        t: forScreen(createLiteTranslator(locale, reach, ENGLISH)),
        pending,
      };
    }
    return {
      locale: shown.locale,
      t: forScreen(createLiteTranslator(shown.locale, shown.messages, ENGLISH)),
    };
  }, [loaded, locale]);
  // I-1925: the gates below compose a refusal's reason as they render, so they read this one.
  setPageTranslator(value.t, value.locale);
  useEffect(() => () => clearPageTranslator(value.t), [value.t]);
  // Layout effect: the page's language changes in the same commit as its text, never a frame after.
  useLayoutEffect(() => {
    document.documentElement.lang = value.locale === 'en-XA' ? 'en' : value.locale;
  }, [value.locale]);
  // T-0471: a store's kept failure is worded again in the language now in force.
  useLayoutEffect(() => announcePageLanguage(), [value.t]);
  // F-0615: the picked region, set before the children format anything, and part of the value so they re-render.
  const region = useSyncExternalStore(
    languagePreference.subscribe,
    () => languagePreference.getSnapshot().region,
  );
  setFormatRegion(region);
  const withRegion = useMemo(() => ({ ...value, region }), [value, region]);
  return <LocaleContext.Provider value={withRegion}>{children}</LocaleContext.Provider>;
}

/**
 * I-1971: how long a screen waits for the reader's own catalogue before it draws in the one at hand. A
 * chunk that is slow or cannot be fetched (a browser keeps that failure for the page) never holds the
 * app longer than this; after it, the page reads as before (I-2088), and re-renders in place once it is in.
 */
export const LANGUAGE_HOLD_CAP_MS = 2_000;

/**
 * I-1971: whether a screen should hold back its words now: the reader's language is known (the session
 * carried it, or the device's cache) and its catalogue is still loading, for at most `LANGUAGE_HOLD_CAP_MS`.
 */
export function useLanguageHold(): boolean {
  const pending = useContext(LocaleContext).pending === true;
  const [capped, setCapped] = useState(false);
  useEffect(() => {
    if (!pending) {
      setCapped(false);
      return undefined;
    }
    const timer = setTimeout(() => setCapped(true), LANGUAGE_HOLD_CAP_MS);
    return () => clearTimeout(timer);
  }, [pending]);
  return pending && !capped;
}

/** The translator of the open page. */
export function useT(): TFn {
  return useContext(LocaleContext).t;
}

/** The language of the open page, for formatters (T-0473). */
export function useLocale(): AnyLocaleType {
  return useContext(LocaleContext).locale;
}
